import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

import {
  GET_DEPOT_SHIFT_MANAGERS_SECTIONS,
  GET_DEPOT_SHIFT_MANAGERS,
  UPDATE_DEPOT_SHIFT_MANAGER,
} from "../../consts/apis";
import {
  SUCCESS_FETCH_DEPOT_SHIFT_MANAGERS,
  ERROR_FETCH_DEPOT_SHIFT_MANAGERS,
  ERROR_FETCH_DEPOT_SECTIONS,
  SUCCESS_FETCH_DEPOT_SECTIONS,
} from "../../consts/messages";

import api from "../../services/api";

import { generateColor } from "../../utils/routes";

import { addNotification, setSelectedShiftManager } from "../ownApp";
import {
  sectionsSelector,
  shiftManagerEditSelector,
  shiftManagerSelector,
  shiftManagersSelector,
} from "./selectors";
import { getSelectedDepot } from "../ownApp/selectors";

export const resetShiftManagerManagement = createAsyncThunk(
  "shiftManagerManagement/resetShiftManagerManagement",
  async (params) => ({})
);
export const setShiftManagerView = createAction(
  "shiftManagerManagement/setShiftManagerView"
);

export const fetchDepotSections = createAsyncThunk(
  "shiftManagerManagement/fetchDepotSections",
  async ({ depotCode }, { dispatch }) => {
    try {
      const {
        data: { sections },
      } = await api.get(GET_DEPOT_SHIFT_MANAGERS_SECTIONS(depotCode));

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_DEPOT_SECTIONS,
          options: { variant: "success" },
        })
      );

      return { sections };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_DEPOT_SECTIONS,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const fetchDepotShiftManagers = createAsyncThunk(
  "shiftManagerManagement/fetchDepotShiftManagers",
  async ({ depotCode }, { dispatch }) => {
    try {
      const {
        data: { shiftManagers },
      } = await api.get(GET_DEPOT_SHIFT_MANAGERS(depotCode));

      dispatch(
        addNotification({
          message: SUCCESS_FETCH_DEPOT_SHIFT_MANAGERS,
          options: { variant: "success" },
        })
      );

      dispatch(setSelectedShiftManager(null));

      return {
        shiftManagers: shiftManagers.map((route, index) => ({
          ...route,
          ...generateColor(index),
        })),
      };
    } catch (error) {
      dispatch(
        addNotification({
          message: ERROR_FETCH_DEPOT_SHIFT_MANAGERS,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const editShiftManager = createAsyncThunk(
  "shiftManagerManagement/editShiftManager",
  async ({ shiftManagerCode }, { dispatch, getState }) => {
    const state = getState();
    const shiftManager = shiftManagerSelector(shiftManagerCode)(state);
    dispatch(setSelectedShiftManager(null));
    return shiftManager;
  }
);

export const updateShiftManagerEdit = createAsyncThunk(
  "shiftManagerManagement/updateShiftManagerEdit",
  async ({ dayOfWeek, name, value }, { getState }) => {
    const state = getState();
    const sections = sectionsSelector(state);
    const shiftManagerEdit = shiftManagerEditSelector(state);
    if (name === "sectionCode") {
      const newSections = shiftManagerEdit.sections.map((section) => {
        if (section.dayOfWeek === dayOfWeek) {
          const { sectionCode, sectionName } = value
            ? sections.find(({ sectionCode }) => sectionCode === value)
            : { sectionCode: null, sectionName: null };
          return {
            dayOfWeek,
            sectionCode,
            sectionName,
          };
        } else return section;
      });
      return {
        ...shiftManagerEdit,
        sections: newSections,
        isActive: !!newSections.find(({ sectionCode }) => !!sectionCode),
      };
    } else
      return {
        ...shiftManagerEdit,
        [name]: value,
      };
  }
);

export const saveShiftManager = createAsyncThunk(
  "shiftManagerManagement/saveShiftManager",
  async (params, { dispatch, getState }) => {
    const state = getState();
    const depotCode = getSelectedDepot(state);
    const shiftManagerEdit = shiftManagerEditSelector(state);
    const shiftManagers = shiftManagersSelector(state);
    try {
      const {
        data: { success },
      } = await api.put(UPDATE_DEPOT_SHIFT_MANAGER(depotCode), null, {
        shiftManagerCode: shiftManagerEdit.shiftManagerCode,
        shiftManagerName: shiftManagerEdit.shiftManagerName,
        restrictedAccess: shiftManagerEdit.restrictedAccess,
        sections: shiftManagerEdit.sections,
      });
      if (!success) throw new Error("Fail to update depot shift manager");

      dispatch(
        addNotification({
          message: `Successfully updated shift manager.`,
          options: { variant: "success" },
        })
      );

      return shiftManagers.map((shiftManager) =>
        shiftManager.shiftManagerCode !== shiftManagerEdit.shiftManagerCode
          ? shiftManager
          : shiftManagerEdit
      );
    } catch (error) {
      dispatch(
        addNotification({
          message: error.message,
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const cancelShiftManagerEdit = createAction(
  "shiftManagerManagement/cancelShiftManagerEdit"
);

export const setIsShiftManagerEditValid = createAction(
  "shiftManagerManagement/setIsShiftManagerEditValid"
);
